import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { getDatabase, ref as dbRef, get } from "firebase/database";
import { DocumentEditModal } from "./DocumentEditModal";
import { MultiFileUploadModal, PendingFileUpload } from "./MultiFileUploadModal";
import { TextDocumentEditor } from "./TextDocumentEditor";
import { BreadcrumbComponent, BreadcrumbItem } from "../../Components/BreadCrumb";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useFeature } from "../../hooks/useFeatures";
import { useBuilding } from "../../Helpers/BuildingContext";
import { FileText, File, Upload, Edit3, Calendar, ChevronDown, ChevronLeft, ChevronRight, Download, ExternalLink, MoreVertical, Plus } from "lucide-react";
import "../../MapOverview/FilterPanel.css";
import "./DocumentsPage.css";
import { PhotoModel, ComponentDocument } from "../../types/protocols";
import { extractComponentDocuments, extractRiskAttachments } from "../../Helpers/buildingDocuments";
import { leasesApi } from "../../api/leasesApi";
import { workOrdersApi } from "../../api/workOrdersApi";
import { Lease } from "../../types/leases";
import { WorkOrder } from "../../types/workOrders";
import { TimelineAttachment } from "../../types/timeline";
import { leaseObjectLabel, leasesPath } from "../../Helpers/leaseUtils";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { currentDrawings } from "../../Helpers/blueprintLookup";
import { FileAttachment, Attachment, isImageAttachment } from "../../types/attachments";
import { useBuildComponentTypes } from "../../contexts/BuildComponentTypesContext";
import { projectsApi, ProjectWithBuilding } from "../../api/projectsApi";
import { MaintenancePlan } from "../../Helpers/maintenanceCostUtils";
import { useDocumentCategories } from "../../contexts/DocumentCategoriesContext";
import { buildingDocumentsApi, FileDocument, TextDocument, DocumentModel } from "../../api/buildingDocumentsApi";
import { useDocumentUpload, FileUploadProgress } from "../../hooks/documents/useDocumentUpload";
import { ImagePreview, isImageFileType } from "../../Components/ImagePreview";
import { useUserNames } from "../../Helpers/useUserNames";
import { usePasteUpload } from "../../hooks/usePasteUpload";
import { useIsSmallScreen } from "../../hooks/useIsSmallScreen";

interface CategoryOption {
    value: string;
    label: string;
}

interface DocumentsPageProps {
    title: string;
    allowTextDocuments?: boolean;
    allowFileUploads?: boolean;
}

/**
 * A file owned by a building-scoped record outside the building node
 * (lease contract, work order attachment, timeline event attachment).
 * `ownerLabel` is the second line of the row; `ownerPath` is where it is managed,
 * absent when the user cannot open that page (lease contracts for non-admins).
 */
interface ExternalAttachmentEntry {
    attachment: Attachment;
    ownerLabel: string;
    ownerPath?: string;
}

interface ProjectAttachmentEntry {
    projectKey: string;
    projectTitle: string;
    attachment: Attachment;
}

interface MaintenanceDocumentEntry {
    type: 'template' | 'activity' | 'replacement';
    componentKey?: string;
    componentType?: string;
    templateId?: string;
    templateName?: string;
    /** The attachment's key in its map; what `?doc=` carries. */
    id: string;
    attachment: Attachment | PhotoModel;
}

/**
 * The document `?doc=<key>` points at, resolved against everything the page
 * lists. `key` is the building document's key, the level key of a drawing
 * (`roofPlan` for the roof drawing) or the attachment's key.
 */
type OpenDocument =
    | { kind: 'text'; key: string; doc: TextDocument }
    | { kind: 'file'; key: string; doc: FileDocument; storagePath: string }
    | { kind: 'blueprint' | 'risk' | 'component' | 'project' | 'maintenance' | 'external'; key: string; storagePath: string };

const isFileDocument = (doc: DocumentModel | null | undefined): doc is FileDocument => {
    return doc != null && doc.documentType === 'file';
};

const isTextDocument = (doc: DocumentModel | null | undefined): doc is TextDocument => {
    return doc != null && doc.documentType === 'text';
};

/** A stored building document as a DocumentModel; legacy file documents lack `documentType`. */
const toDocumentModel = (stored: unknown): DocumentModel | null => {
    if (!stored || typeof stored !== 'object') return null;
    const doc = stored as Partial<FileDocument>;
    if (doc.documentType) return stored as DocumentModel;
    if (!doc.fileType) return null;
    return {
        ...doc,
        documentType: 'file',
        category: doc.fileType,
        storageFilename: doc.storageFilename || doc.name // fallback to name if storageFilename missing
    } as FileDocument;
};

const EMPTY_DOCUMENTS: Record<string, any> = {};

const attachmentDisplayName = (attachment: Attachment): string =>
    isImageAttachment(attachment)
        ? (attachment.displayName || attachment.fileName || 'Bilaga')
        : (attachment.displayName || attachment.fileName);

const sortByOwnerThenName = (a: ExternalAttachmentEntry, b: ExternalAttachmentEntry) => {
    const ownerCompare = a.ownerLabel.localeCompare(b.ownerLabel);
    if (ownerCompare !== 0) return ownerCompare;
    return attachmentDisplayName(a.attachment).localeCompare(attachmentDisplayName(b.attachment));
};

const DocumentsPage: React.FC<DocumentsPageProps> = ({
                                                         title,
                                                         allowTextDocuments = true,
                                                         allowFileUploads = true
                                                     }) => {
    // Set document title
    useDocumentTitle(title);

    const { categories: documentCategories, loading: categoriesLoading } = useDocumentCategories();
    const customFileTypeOptions: CategoryOption[] = useMemo(
        () => documentCategories.map(c => ({ value: c.key, label: c.label })),
        [documentCategories]
    );
    const [groupedDocuments, setGroupedDocuments] = useState<Record<string, Array<{dbKey: string, doc: DocumentModel}>>>({});
    const [isDragging, setIsDragging] = useState(false);
    const [draggingOverCategory, setDraggingOverCategory] = useState<string | null>(null);
    const [defaultUploadCategory, setDefaultUploadCategory] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedSections, setSelectedSections] = useState<Set<string>>(() => new Set());
    const [error, setError] = useState<string | null>(null);

    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [showFileModal, setShowFileModal] = useState(false);
    const [editDoc, setEditDoc] = useState<null | {
        dbKey: string;
        fileData: FileDocument;
    }>(null);

    const [showTextEditor, setShowTextEditor] = useState(false);
    const [editingTextDoc, setEditingTextDoc] = useState<{
        dbKey: string;
        document: TextDocument;
    } | null>(null);

    // Download URL of the open file, tagged with the path it was fetched for
    const [previewUrl, setPreviewUrl] = useState<{ storagePath: string; url: string } | null>(null);
    const [leases, setLeases] = useState<Lease[]>([]);
    const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
    const [projects, setProjects] = useState<ProjectWithBuilding[]>([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [maintenancePlan, setMaintenancePlan] = useState<MaintenancePlan | null>(null);
    const [maintenanceLoading, setMaintenanceLoading] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const { id: buildingKey } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    // The open document lives in the URL (`?doc=<key>`), so it survives a
    // reload, can be linked to, and the browser's back button closes it.
    const [searchParams, setSearchParams] = useSearchParams();
    const docFromUrl = searchParams.get('doc');
    const { getTranslation, getTemplateById } = useBuildComponentTypes();

    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();

    // Base documents from building context
    const buildingDocuments: Record<string, any> =
        currentBuilding?.model?.documents ?? EMPTY_DOCUMENTS;

    // Local state for immediate UI updates (additions, updates, deletions)
    // null value means the document was deleted
    const [localDocumentUpdates, setLocalDocumentUpdates] = useState<Record<string, DocumentModel | null>>({});

    // Merge building documents with local updates for display
    const documents: Record<string, any> = useMemo(() => {
        const merged = { ...buildingDocuments };
        for (const [key, value] of Object.entries(localDocumentUpdates)) {
            if (value === null) {
                delete merged[key];
            } else {
                merged[key] = value;
            }
        }
        return merged;
    }, [buildingDocuments, localDocumentUpdates]);

    // Use existing useUserNames hook instead of custom implementation
    const { getUserName } = useUserNames();

    // Use document upload hook with throttled progress updates
    const {
        uploadProgress,
        isUploading,
        uploadFiles
    } = useDocumentUpload({
        throttleMs: 150,
        onComplete: () => {
            // No need to refetch - we update local state directly
        },
        onError: (error) => setError(error)
    });

    // UserName display component using the hook
    const UserName: React.FC<{ uid: string | undefined }> = ({ uid }) => {
        const [userName, setUserName] = useState<string>('Laddar...');

        useEffect(() => {
            if (!uid) {
                setUserName('Okänd användare');
                return;
            }
            getUserName(uid).then(setUserName);
        }, [uid]);

        return <span>{userName}</span>;
    };

    // Helper function to get download URL from storage filename
    const getFileUrl = async (storageFilename: string): Promise<string> => {
        return buildingDocumentsApi.getDocumentUrl(buildingKey!, storageFilename);
    };

    useEffect(() => {
        if (buildingKey) {
            fetchBuilding(buildingKey).then();
        }
    }, [buildingKey, fetchBuilding]);

    // Fetch projects for this building
    useEffect(() => {
        const loadProjects = async () => {
            if (!buildingKey) return;
            setProjectsLoading(true);
            try {
                const projectsData = await projectsApi.getProjectsByBuilding(buildingKey);
                setProjects(projectsData);
            } catch (err) {
                console.error('Error loading projects:', err);
            } finally {
                setProjectsLoading(false);
            }
        };
        loadProjects();
    }, [buildingKey]);

    // Fetch maintenance plan for this building
    useEffect(() => {
        const loadMaintenancePlan = async () => {
            if (!buildingKey) return;
            setMaintenanceLoading(true);
            try {
                const db = getDatabase();
                const planSnapshot = await get(dbRef(db, `maintenance/active/${buildingKey}`));
                if (planSnapshot.exists()) {
                    setMaintenancePlan(planSnapshot.val() as MaintenancePlan);
                } else {
                    setMaintenancePlan(null);
                }
            } catch (err) {
                console.error('Error loading maintenance plan:', err);
            } finally {
                setMaintenanceLoading(false);
            }
        };
        loadMaintenancePlan();
    }, [buildingKey]);

    // Fetch leases and work orders (active + archived) for this building, in
    // parallel with the project and maintenance fetches above.
    useEffect(() => {
        if (!buildingKey) return;
        let cancelled = false;
        // Drop the previous building's files while the new fetch is in flight.
        setLeases([]);
        setWorkOrders([]);
        leasesApi.getByBuilding(buildingKey)
            .then(result => { if (!cancelled) setLeases(result); })
            .catch(err => console.error('Error loading leases:', err));
        Promise.all([
            workOrdersApi.getByBuilding(buildingKey),
            workOrdersApi.getArchivedByBuilding(buildingKey),
        ])
            .then(([active, archived]) => { if (!cancelled) setWorkOrders([...active, ...archived]); })
            .catch(err => console.error('Error loading work orders:', err));
        return () => { cancelled = true; };
    }, [buildingKey]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenDropdown(null);
        if (openDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openDropdown]);

    // Special sections (non-document-category sections)
    const SPECIAL_SECTIONS = ['Planritningar', 'Arbetsmiljörisker', 'Komponentdokument', 'Projektbilagor', 'Hyresavtal', 'Ärenden', 'Tidslinje', 'Underhållsdokument'];

    // Get all available sections (document categories + special sections)
    const allSections = useMemo(() => {
        const docCategories = customFileTypeOptions.map(c => c.value);
        return [...docCategories, ...SPECIAL_SECTIONS];
    }, [customFileTypeOptions]);

    // Initialize selectedSections with all sections when allSections changes
    useEffect(() => {
        if (selectedSections.size === 0 && allSections.length > 0) {
            setSelectedSections(new Set(allSections));
        }
    }, [allSections, selectedSections.size]);

    // Check if all sections are selected
    const allSectionsSelected = useMemo(() => {
        return allSections.every(section => selectedSections.has(section));
    }, [allSections, selectedSections]);

    // Count of filtered out sections
    const filteredOutCount = useMemo(() => {
        return allSections.length - selectedSections.size;
    }, [allSections, selectedSections]);

    // Handle toggling a section filter
    const handleSectionToggle = useCallback((section: string) => {
        setSelectedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    }, []);

    // Reset filters to show all
    const handleResetFilters = useCallback(() => {
        setSelectedSections(new Set(allSections));
    }, [allSections]);

    useEffect(() => {
        const convertedDocuments: Record<string, DocumentModel> = {};

        Object.entries(documents).forEach(([dbKey, stored]) => {
            const doc = toDocumentModel(stored);
            if (doc) convertedDocuments[dbKey] = doc;
        });

        // Filter entries based on selected sections
        const knownKeys = new Set(customFileTypeOptions.map(c => c.value));
        const filteredEntries = Object.entries(convertedDocuments).filter(([dbKey, doc]) => {
            const category = (doc.category && knownKeys.has(doc.category)) ? doc.category : 'Övrigt';
            return selectedSections.has(category);
        });

        // Group documents by category
        const grouped: Record<string, Array<{dbKey: string, doc: DocumentModel}>> = {};

        // Initialize all known categories as empty arrays (so they show even with 0 docs)
        customFileTypeOptions.forEach(option => {
            grouped[option.value] = [];
        });

        // Group by individual categories, mapping unknown categories to Övrigt
        filteredEntries.forEach(([dbKey, doc]) => {
            const category = (doc.category && knownKeys.has(doc.category)) ? doc.category : 'Övrigt';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push({ dbKey, doc });
        });

        // Sort documents within each category
        Object.keys(grouped).forEach(category => {
            grouped[category].sort((a, b) =>
                a.doc.name.toLowerCase().localeCompare(b.doc.name.toLowerCase())
            );
        });

        setGroupedDocuments(grouped);

        // Sections start collapsed (a building can hold hundreds of
        // documents); a section the user has opened or closed keeps that state.
        setCollapsedSections(prev => {
            const newCollapsedSections: Record<string, boolean> = { ...prev };
            [...Object.keys(grouped), ...SPECIAL_SECTIONS].forEach(section => {
                if (newCollapsedSections[section] === undefined) {
                    newCollapsedSections[section] = true;
                }
            });
            return newCollapsedSections;
        });
    }, [documents, selectedSections, customFileTypeOptions]);

    const selectableCategories = customFileTypeOptions.map(opt => opt.value);
    const relevantDocuments = Object.values(documents).filter(doc => {
        const category = doc.category || doc.fileType;
        return selectableCategories.includes(category || '');
    });

    const totalDocuments = relevantDocuments.length;
    const categoryCount = relevantDocuments.reduce(
        (acc: Record<string, number>, doc: any) => {
            const category = doc.category || doc.fileType || 'unknown';
            acc[category] = (acc[category] || 0) + 1;
            return acc;
        },
        {}
    );

    // Current drawings: levels by number descending, then the roof drawing ("Takplan")
    const levelBlueprints = useMemo(
        (): Array<{ levelKey: string; label: string; blueprint: PhotoModel }> =>
            currentDrawings(currentBuilding?.model?.structure)
                .sort((a, b) => {
                    if (a.owner.kind !== b.owner.kind) return a.owner.kind === 'roofPlan' ? 1 : -1;
                    return b.order - a.order;
                })
                .map(drawing => ({
                    levelKey: drawing.owner.kind === 'level' ? drawing.owner.level.key : 'roofPlan',
                    label: drawing.label,
                    blueprint: drawing.image,
                })),
        [currentBuilding?.model?.structure]
    );

    // Risk attachments from all rooms and documents from all non-archived
    // components, via the helper the building page counter shares.
    const riskAttachments = useMemo(
        () => extractRiskAttachments(currentBuilding?.model?.structure?.rooms),
        [currentBuilding?.model?.structure?.rooms]
    );

    const componentDocuments = useMemo(
        () => extractComponentDocuments(currentBuilding?.model?.structure?.components),
        [currentBuilding?.model?.structure?.components]
    );

    // Lease contracts (leases/{key}/contracts), managed from the lease list; none while renting is off
    const tenantsEnabled = useFeature('tenants');
    const isAdmin = useIsAdmin();
    const leaseContracts = useMemo(() => {
        const results: ExternalAttachmentEntry[] = [];
        if (!tenantsEnabled) return results;
        leases.forEach(lease => {
            Object.values(lease.contracts ?? {}).forEach(contract => {
                results.push({
                    attachment: contract,
                    ownerLabel: leaseObjectLabel(lease),
                    ownerPath: isAdmin && buildingKey ? leasesPath(buildingKey, lease.key) : undefined,
                });
            });
        });
        return results.sort(sortByOwnerThenName);
    }, [leases, buildingKey, tenantsEnabled, isAdmin]);

    // Work order attachments (active and archived), managed from the work order
    const workOrderAttachments = useMemo(() => {
        const results: ExternalAttachmentEntry[] = [];
        workOrders.forEach(workOrder => {
            Object.values(workOrder.attachments ?? {}).forEach(attachment => {
                results.push({
                    attachment,
                    ownerLabel: workOrder.title,
                    ownerPath: `/management?wo=${workOrder.key}`,
                });
            });
        });
        return results.sort(sortByOwnerThenName);
    }, [workOrders]);

    // Custom timeline event attachments (buildings/{key}/timelineItems), managed from the timeline
    const timelineAttachments = useMemo(() => {
        const results: ExternalAttachmentEntry[] = [];
        Object.values(currentBuilding?.model?.timelineItems ?? {}).forEach(item => {
            Object.values(item.attachments ?? {}).forEach((attachment: TimelineAttachment) => {
                results.push({
                    attachment,
                    ownerLabel: item.title || item.description || item.date,
                    ownerPath: `/building/${buildingKey}/timeline`,
                });
            });
        });
        return results.sort(sortByOwnerThenName);
    }, [currentBuilding?.model?.timelineItems, buildingKey]);

    // Extract attachments from all projects
    const projectAttachments = useMemo(() => {
        const results: ProjectAttachmentEntry[] = [];

        projects.forEach(project => {
            const attachments = project.attachments;
            if (attachments) {
                Object.values(attachments).forEach((attachment: Attachment) => {
                    results.push({
                        projectKey: project.key,
                        projectTitle: project.title,
                        attachment
                    });
                });
            }
        });

        // Sort by project title, then attachment name
        results.sort((a, b) => {
            const titleCompare = a.projectTitle.localeCompare(b.projectTitle);
            if (titleCompare !== 0) return titleCompare;
            const aName = isImageAttachment(a.attachment) ? (a.attachment.displayName || a.attachment.fileName || '') : (a.attachment.displayName || a.attachment.fileName);
            const bName = isImageAttachment(b.attachment) ? (b.attachment.displayName || b.attachment.fileName || '') : (b.attachment.displayName || b.attachment.fileName);
            return aName.localeCompare(bName);
        });

        return results;
    }, [projects]);

    // Extract documents from maintenance plan (only templates in use for this building)
    const maintenanceDocuments = useMemo(() => {
        if (!maintenancePlan?.components) return [];

        const results: MaintenanceDocumentEntry[] = [];
        const usedTemplateIds = new Set<string>();

        // Extract from each component's maintenance data
        Object.entries(maintenancePlan.components).forEach(([componentKey, compData]) => {
            const componentType = currentBuilding?.model?.structure?.components?.[componentKey]?.specificType?.value || '';

            // Cost attachments from activities
            if (compData.maintenanceActivities) {
                Object.entries(compData.maintenanceActivities).forEach(([templateId, activity]) => {
                    usedTemplateIds.add(templateId);
                    if (activity.costAttachments) {
                        Object.entries(activity.costAttachments).forEach(([id, att]) => {
                            results.push({
                                type: 'activity',
                                componentKey,
                                componentType,
                                templateId,
                                templateName: getTemplateById(templateId)?.name,
                                id,
                                attachment: att
                            });
                        });
                    }
                });
            }

            // Replacement attachments
            if (compData.replacementAttachments) {
                Object.entries(compData.replacementAttachments).forEach(([id, att]) => {
                    results.push({
                        type: 'replacement',
                        componentKey,
                        componentType,
                        id,
                        attachment: att
                    });
                });
            }
        });

        // Template instructional attachments (only for templates in use)
        usedTemplateIds.forEach(templateId => {
            const template = getTemplateById(templateId);
            if (template?.instructionalAttachments) {
                Object.entries(template.instructionalAttachments).forEach(([id, att]) => {
                    results.push({
                        type: 'template',
                        templateId,
                        templateName: template.name,
                        id,
                        attachment: att
                    });
                });
            }
        });

        // Sort by type, then by component/template name
        results.sort((a, b) => {
            const typeOrder = { template: 0, activity: 1, replacement: 2 };
            const typeCompare = typeOrder[a.type] - typeOrder[b.type];
            if (typeCompare !== 0) return typeCompare;

            // For template type, sort by template name
            if (a.type === 'template' && b.type === 'template') {
                return (a.templateName || '').localeCompare(b.templateName || '');
            }

            // For activity/replacement, sort by component type
            return (a.componentType || '').localeCompare(b.componentType || '');
        });

        return results;
    }, [maintenancePlan, currentBuilding?.model?.structure?.components, getTemplateById]);

    // Resolve `?doc=` against every list on the page, unfiltered, so a hidden
    // section does not close the open document. A key that matches nothing
    // (deleted file, or a list that is still loading) opens nothing.
    const openDocument = useMemo((): OpenDocument | null => {
        if (!docFromUrl || !buildingKey) return null;

        const buildingDoc = toDocumentModel(documents[docFromUrl]);
        if (isTextDocument(buildingDoc)) {
            return { kind: 'text', key: docFromUrl, doc: buildingDoc };
        }
        if (isFileDocument(buildingDoc)) {
            return {
                kind: 'file',
                key: docFromUrl,
                doc: buildingDoc,
                storagePath: buildingDocumentsApi.getStoragePath(buildingKey, buildingDoc.storageFilename),
            };
        }

        const drawing = levelBlueprints.find(entry => entry.levelKey === docFromUrl);
        if (drawing) {
            return { kind: 'blueprint', key: docFromUrl, storagePath: drawing.blueprint.storagePath };
        }
        const risk = riskAttachments.find(entry => entry.attachment.key === docFromUrl);
        if (risk) {
            return { kind: 'risk', key: docFromUrl, storagePath: risk.attachment.storagePath };
        }
        const componentDocument = componentDocuments.find(entry => entry.document.key === docFromUrl);
        if (componentDocument) {
            return {
                kind: 'component',
                key: docFromUrl,
                storagePath: `buildings/${buildingKey}/structure/components/${componentDocument.componentKey}/documents/${componentDocument.document.storageFilename}`,
            };
        }
        const projectAttachment = projectAttachments.find(entry => entry.attachment.key === docFromUrl);
        if (projectAttachment) {
            return { kind: 'project', key: docFromUrl, storagePath: projectAttachment.attachment.storagePath };
        }
        const external = [...leaseContracts, ...workOrderAttachments, ...timelineAttachments]
            .find(entry => entry.attachment.key === docFromUrl);
        if (external) {
            return { kind: 'external', key: docFromUrl, storagePath: external.attachment.storagePath };
        }
        const maintenance = maintenanceDocuments.find(entry => entry.id === docFromUrl);
        if (maintenance?.attachment.storagePath) {
            return { kind: 'maintenance', key: docFromUrl, storagePath: maintenance.attachment.storagePath };
        }
        return null;
    }, [
        docFromUrl, buildingKey, documents, levelBlueprints, riskAttachments, componentDocuments,
        projectAttachments, leaseContracts, workOrderAttachments, timelineAttachments, maintenanceDocuments,
    ]);

    const expandSection = useCallback((section: string) => {
        setCollapsedSections(prev => (prev[section] === false ? prev : { ...prev, [section]: false }));
    }, []);

    const categorySection = useCallback((category: string | undefined) => (
        category && customFileTypeOptions.some(opt => opt.value === category) ? category : 'Övrigt'
    ), [customFileTypeOptions]);

    // The section that lists the open document, so a linked document is not
    // hidden in a collapsed section.
    const openDocumentSection = useMemo((): string | null => {
        if (!openDocument) return null;
        switch (openDocument.kind) {
            case 'text':
            case 'file': return categorySection(openDocument.doc.category);
            case 'blueprint': return 'Planritningar';
            case 'risk': return 'Arbetsmiljörisker';
            case 'component': return 'Komponentdokument';
            case 'project': return 'Projektbilagor';
            case 'maintenance': return 'Underhållsdokument';
            case 'external':
                if (leaseContracts.some(entry => entry.attachment.key === openDocument.key)) return 'Hyresavtal';
                if (workOrderAttachments.some(entry => entry.attachment.key === openDocument.key)) return 'Ärenden';
                return 'Tidslinje';
        }
    }, [openDocument, categorySection, leaseContracts, workOrderAttachments]);

    // Opened once per document: closing the section afterwards sticks.
    const openDocumentKey = openDocument?.key ?? null;
    useEffect(() => {
        if (openDocumentSection) expandSection(openDocumentSection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openDocumentKey]);

    const openKey = (kind: OpenDocument['kind']) => (openDocument?.kind === kind ? openDocument.key : null);
    const selectedTextDoc = openKey('text');
    const selectedFileDoc = openKey('file');
    const selectedRiskAttachment = openKey('risk');
    const selectedComponentDocument = openKey('component');
    const selectedProjectAttachment = openKey('project');
    const selectedExternalAttachment = openKey('external');
    const selectedMaintenanceDocument = openKey('maintenance');
    const selectedBlueprint = openDocument?.kind === 'blueprint' ? openDocument.storagePath : null;
    const openBuildingDocument = openDocument && 'doc' in openDocument ? openDocument.doc : null;

    // Opening from the list adds a history entry, so the browser's back button
    // (and the back gesture on phones) closes the document. Switching between
    // documents replaces it: one step back always lands on the list.
    const openedFromList = !!(location.state as { fromDocumentList?: boolean } | null)?.fromDocumentList;

    const showDocument = (key: string) => {
        if (key === docFromUrl) return;
        setSearchParams(prev => {
            prev.set('doc', key);
            return prev;
        }, openDocument
            ? { replace: true, state: location.state }
            : { state: { fromDocumentList: true } });
    };

    // A linked document has no list entry behind it, so its parameter is
    // dropped in place instead of stepping back.
    const closeDocument = useCallback(() => {
        if (openedFromList) {
            navigate(-1);
            return;
        }
        setSearchParams(prev => {
            prev.delete('doc');
            return prev;
        }, { replace: true });
    }, [openedFromList, navigate, setSearchParams]);

    // Files are shown from a download URL; text documents and drawings need none.
    const previewStoragePath = openDocument && openDocument.kind !== 'text' && openDocument.kind !== 'blueprint'
        ? openDocument.storagePath
        : null;
    const selectedPdf = previewUrl && previewUrl.storagePath === previewStoragePath ? previewUrl.url : null;

    useEffect(() => {
        if (!previewStoragePath || previewUrl?.storagePath === previewStoragePath) return;
        let cancelled = false;
        buildingDocumentsApi.getUrlFromStoragePath(previewStoragePath)
            .then(url => {
                if (!cancelled) setPreviewUrl({ storagePath: previewStoragePath, url });
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Error loading file:', err);
                setError('Kunde inte ladda filen.');
                closeDocument();
            });
        return () => { cancelled = true; };
    }, [previewStoragePath, previewUrl?.storagePath, closeDocument]);

    const handleDeleteDoc = async () => {
        if (!editDoc || !buildingKey) return;

        try {
            await buildingDocumentsApi.deleteDocument(
                buildingKey,
                editDoc.dbKey,
                editDoc.fileData.storageFilename
            );
            // Update local state immediately
            setLocalDocumentUpdates(prev => ({
                ...prev,
                [editDoc.dbKey]: null
            }));
            if (editDoc.dbKey === docFromUrl) closeDocument();
        } catch (error) {
            console.error("Error deleting file:", error);
            setError("Kunde inte radera filen. Försök igen.");
        } finally {
            setEditDoc(null);
        }
    };

    const handleDrop = (event: React.DragEvent, category?: string) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        setDraggingOverCategory(null);

        const files = Array.from(event.dataTransfer.files);
        if (files.length === 0) return;

        setDefaultUploadCategory(category || null);
        setPendingFiles(files);
        setShowFileModal(true);
    };

    // Allow pasting files (Ctrl/Cmd + V) to start an upload, mirroring the drop flow.
    // The drop handler accepts all file types, so no type filter is applied here either.
    const anyModalOpen = showFileModal || !!editDoc || showTextEditor || !!editingTextDoc;
    usePasteUpload({
        enabled: allowFileUploads && !anyModalOpen && !isUploading,
        onFilesPasted: (files) => {
            if (files.length === 0) return;
            setDefaultUploadCategory(null);
            setPendingFiles(files);
            setShowFileModal(true);
        }
    });

    // Phones have no drag and drop or paste, so they get a file picker instead.
    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length === 0) return;
        setDefaultUploadCategory(null);
        setPendingFiles(files);
        setShowFileModal(true);
    };

    // Phones show either the list or the open document, never both. Going back
    // returns the list to where it was scrolled.
    const isSmallScreen = useIsSmallScreen();
    const hasPreview = !!openDocument;
    const mobileView = isSmallScreen && hasPreview ? 'preview' : 'list';
    const pageRef = useRef<HTMLDivElement>(null);
    const listScrollTopRef = useRef(0);

    useLayoutEffect(() => {
        const scroller = pageRef.current?.closest('.main-content');
        if (!scroller) return;
        if (mobileView === 'list') {
            scroller.scrollTop = listScrollTopRef.current;
        } else {
            scroller.scrollTop = 0;
        }
    }, [mobileView]);

    // A linked document may sit far down the list: bring its row into view
    // once the list has rendered it.
    const scrolledToOpenRef = useRef(false);
    useEffect(() => {
        if (scrolledToOpenRef.current || !openDocument) return;
        const row = pageRef.current?.querySelector('.documents-list-panel .list-group-item.active');
        if (!row) return;
        scrolledToOpenRef.current = true;
        row.scrollIntoView({ block: 'nearest' });
    }, [openDocument, groupedDocuments, collapsedSections]);

    const rememberListScroll = () => {
        listScrollTopRef.current = pageRef.current?.closest('.main-content')?.scrollTop ?? 0;
    };

    const handleConfirmMultipleFiles = async (uploads: PendingFileUpload[]) => {
        setError(null);
        if (uploads.length === 0 || !buildingKey) return;

        // Use the upload hook - it handles concurrent uploads and progress
        const uploadedDocs = await uploadFiles(buildingKey, uploads);

        // Update local state with uploaded documents
        if (uploadedDocs.length > 0) {
            uploadedDocs.forEach(doc => expandSection(categorySection(doc.category)));
            setLocalDocumentUpdates(prev => {
                const updates = { ...prev };
                uploadedDocs.forEach(doc => {
                    updates[doc.key] = doc;
                });
                return updates;
            });
        }

        // Close modal after uploads complete
        setShowFileModal(false);
        setPendingFiles([]);
        setDefaultUploadCategory(null);
    };

    const handleCancelMultipleFiles = () => {
        setShowFileModal(false);
        setPendingFiles([]);
        setDefaultUploadCategory(null);
    };

    const handleEditFileConfirm = async (newName: string, newCategory: string) => {
        if (!editDoc || !buildingKey) return;
        try {
            const updatedDoc = await buildingDocumentsApi.updateDocument(
                buildingKey,
                editDoc.dbKey,
                editDoc.fileData,
                { name: newName, category: newCategory }
            );
            // Update local state immediately
            expandSection(categorySection(newCategory));
            setLocalDocumentUpdates(prev => ({
                ...prev,
                [editDoc.dbKey]: updatedDoc
            }));
        } catch (error) {
            console.error("Error updating file:", error);
            setError("Något gick fel vid uppdateringen.");
        } finally {
            setEditDoc(null);
        }
    };

    const handleCreateTextDoc = async (title: string, content: string, category: string) => {
        setError(null);
        if (!buildingKey) return;

        try {
            const newDoc = await buildingDocumentsApi.createTextDocument(buildingKey, title, content, category);
            // Update local state immediately
            expandSection(categorySection(category));
            setLocalDocumentUpdates(prev => ({
                ...prev,
                [newDoc.key]: newDoc
            }));
            setShowTextEditor(false);
        } catch (error) {
            setError("Något gick fel vid sparande av textdokumentet.");
            console.error("Error saving text document:", error);
        }
    };

    const handleEditTextDoc = async (title: string, content: string, category: string) => {
        if (!editingTextDoc || !buildingKey) return;

        try {
            const updatedDoc = await buildingDocumentsApi.updateTextDocument(
                buildingKey,
                editingTextDoc.dbKey,
                editingTextDoc.document,
                { name: title, textContent: content, category }
            );
            // Update local state immediately
            expandSection(categorySection(category));
            setLocalDocumentUpdates(prev => ({
                ...prev,
                [editingTextDoc.dbKey]: updatedDoc
            }));
        } catch (error) {
            console.error("Error updating text document:", error);
            setError("Något gick fel vid uppdateringen.");
        } finally {
            setEditingTextDoc(null);
        }
    };

    const handleDeleteTextDoc = async () => {
        if (!editingTextDoc || !buildingKey) return;

        try {
            await buildingDocumentsApi.deleteTextDocument(buildingKey, editingTextDoc.dbKey);
            // Update local state immediately
            setLocalDocumentUpdates(prev => ({
                ...prev,
                [editingTextDoc.dbKey]: null
            }));
            if (editingTextDoc.dbKey === docFromUrl) closeDocument();
        } catch (error) {
            console.error("Error deleting text document:", error);
            setError("Kunde inte radera dokumentet. Försök igen.");
        } finally {
            setEditingTextDoc(null);
        }
    };

    const handleEditClick = (dbKey: string, doc: DocumentModel) => {
        if (isTextDocument(doc)) {
            setEditingTextDoc({ dbKey, document: doc });
        } else if (isFileDocument(doc)) {
            setEditDoc({ dbKey, fileData: doc });
        }
    };

    const handleDownloadClick = async (doc: DocumentModel) => {
        try {
            if (isTextDocument(doc)) {
                // Create a text file from the content
                const blob = new Blob([doc.textContent], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${doc.name}.txt`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else if (isFileDocument(doc)) {
                const url = await getFileUrl(doc.storageFilename);
                const link = document.createElement('a');
                link.href = url;
                link.download = doc.name;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            setError('Kunde inte ladda ner filen.');
        }
    };

    const handleDownloadBlueprint = async (blueprint: PhotoModel, levelName: string) => {
        try {
            const storage = getStorage();
            const fileRef = storageRef(storage, blueprint.storagePath);
            const url = await getDownloadURL(fileRef);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${levelName}.${blueprint.storagePath.split('.').pop() || 'png'}`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading blueprint:', error);
            setError('Kunde inte ladda ner ritningen.');
        }
    };

    const handleDownloadRiskAttachment = async (attachment: FileAttachment) => {
        try {
            const storage = getStorage();
            const fileRef = storageRef(storage, attachment.storagePath);
            const url = await getDownloadURL(fileRef);
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.displayName || attachment.fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading risk attachment:', error);
            setError('Kunde inte ladda ner filen.');
        }
    };

    const handleDownloadComponentDocument = async (componentKey: string, document: ComponentDocument) => {
        try {
            const storage = getStorage();
            const storagePath = `buildings/${buildingKey}/structure/components/${componentKey}/documents/${document.storageFilename}`;
            const fileRef = storageRef(storage, storagePath);
            const url = await getDownloadURL(fileRef);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = document.name;
            link.target = '_blank';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading component document:', error);
            setError('Kunde inte ladda ner filen.');
        }
    };

    const handleDownloadProjectAttachment = async (attachment: Attachment) => {
        try {
            const storage = getStorage();
            const storagePath = isImageAttachment(attachment) ? attachment.storagePath : attachment.storagePath;
            const fileRef = storageRef(storage, storagePath);
            const url = await getDownloadURL(fileRef);
            const link = window.document.createElement('a');
            link.href = url;
            const fileName = isImageAttachment(attachment)
                ? (attachment.displayName || attachment.fileName || 'bilaga')
                : (attachment.displayName || attachment.fileName);
            link.download = fileName;
            link.target = '_blank';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading project attachment:', error);
            setError('Kunde inte ladda ner filen.');
        }
    };

    const handleDownloadExternalAttachment = async (attachment: Attachment) => {
        try {
            const storage = getStorage();
            const fileRef = storageRef(storage, attachment.storagePath);
            const url = await getDownloadURL(fileRef);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = attachmentDisplayName(attachment);
            link.target = '_blank';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading attachment:', error);
            setError('Kunde inte ladda ner filen.');
        }
    };

    /**
     * One read-only section for files owned by records outside the building
     * node. Same look as Projektbilagor; the dropdown offers download and a
     * jump to the owning page.
     */
    const renderExternalSection = (
        sectionKey: string,
        entries: ExternalAttachmentEntry[],
        managedFrom: string,
        goToLabel: string
    ) => selectedSections.has(sectionKey) && (
        <div className="mb-4">
            <button
                className="btn btn-link text-start p-0 w-100 text-decoration-none"
                onClick={() => toggleSectionCollapse(sectionKey)}
                style={{ border: "none", background: "none" }}
            >
                <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                    <span>
                        {sectionKey} ({entries.length})
                    </span>
                    {collapsedSections[sectionKey] ? (
                        <ChevronRight size={16} />
                    ) : (
                        <ChevronDown size={16} />
                    )}
                </h5>
            </button>

            {!collapsedSections[sectionKey] && (
                <>
                <p className="text-muted small fst-italic mb-2" style={{ marginTop: '-0.5rem' }}>
                    {managedFrom}
                </p>
                <div className="list-group list-group-flush">
                    {entries.map(({ attachment, ownerLabel, ownerPath }) => {
                        const isSelected = selectedExternalAttachment === attachment.key;
                        const dropdownKey = `${sectionKey}-att-${attachment.key}`;
                        return (
                            <div key={attachment.key} className="d-flex align-items-center gap-2 mb-2">
                                <div
                                    className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${isSelected ? "active" : ""}`}
                                    style={{
                                        cursor: "pointer",
                                        whiteSpace: "normal",
                                        overflowWrap: "break-word",
                                        wordBreak: "break-word",
                                        backgroundColor: isSelected ? "rgb(0, 29, 96)" : undefined,
                                        color: isSelected ? "white" : undefined,
                                        borderColor: isSelected ? "rgb(0, 29, 96)" : undefined,
                                    }}
                                    onClick={() => showDocument(attachment.key)}
                                >
                                    <File
                                        className={isSelected ? "flex-shrink-0" : "text-secondary flex-shrink-0"}
                                        size={20}
                                        color={isSelected ? "white" : undefined}
                                    />
                                    <span className="flex-grow-1">
                                        {attachmentDisplayName(attachment)}
                                        <small className="d-block" style={{ opacity: 0.7 }}>
                                            {ownerLabel}
                                        </small>
                                    </span>
                                </div>
                                <div className="dropdown" style={{ position: 'relative' }}>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
                                        }}
                                    >
                                        <MoreVertical size={14} />
                                    </button>
                                    {openDropdown === dropdownKey && (
                                        <ul
                                            className="dropdown-menu dropdown-menu-end show"
                                            style={{ position: 'absolute', right: 0, top: '100%' }}
                                        >
                                            <li>
                                                <button
                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenDropdown(null);
                                                        handleDownloadExternalAttachment(attachment);
                                                    }}
                                                >
                                                    <Download size={14} />
                                                    Ladda ner
                                                </button>
                                            </li>
                                            {ownerPath && (
                                                <li>
                                                    <Link
                                                        className="dropdown-item d-flex align-items-center gap-2"
                                                        to={ownerPath}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdown(null);
                                                        }}
                                                    >
                                                        <ChevronRight size={14} />
                                                        {goToLabel}
                                                    </Link>
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                </>
            )}
        </div>
    );

    const getMaintenanceDocumentStoragePath = (entry: MaintenanceDocumentEntry): string | null => {
        if (entry.type === 'template') {
            // Template instructional attachments use PhotoModel with storagePath
            const photo = entry.attachment as PhotoModel;
            return photo.storagePath || null;
        } else {
            // Activity/replacement attachments use Attachment type
            const att = entry.attachment as Attachment;
            return att.storagePath || null;
        }
    };

    const getMaintenanceDocumentName = (entry: MaintenanceDocumentEntry): string => {
        if (entry.type === 'template') {
            // PhotoModel doesn't have a name - use template name as context
            return entry.templateName || 'Instruktion';
        } else {
            const att = entry.attachment as Attachment;
            return isImageAttachment(att)
                ? (att.displayName || att.fileName || 'Bilaga')
                : (att.displayName || att.fileName);
        }
    };

    const handleMaintenanceDocumentClick = (entry: MaintenanceDocumentEntry) => {
        if (!getMaintenanceDocumentStoragePath(entry)) {
            setError('Kunde inte hitta filen.');
            return;
        }
        showDocument(entry.id);
    };

    const handleDownloadMaintenanceDocument = async (entry: MaintenanceDocumentEntry) => {
        try {
            const storagePath = getMaintenanceDocumentStoragePath(entry);
            if (!storagePath) {
                setError('Kunde inte hitta filen.');
                return;
            }

            const storage = getStorage();
            const fileRef = storageRef(storage, storagePath);
            const url = await getDownloadURL(fileRef);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = getMaintenanceDocumentName(entry);
            link.target = '_blank';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading maintenance document:', error);
            setError('Kunde inte ladda ner filen.');
        }
    };

    const getCategoryDisplayName = (categoryValue: string): string => {
        const categoryOption = customFileTypeOptions.find(opt => opt.value === categoryValue);
        return categoryOption ? categoryOption.label : categoryValue;
    };

    const toggleSectionCollapse = (category: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const breadcrumbItems: BreadcrumbItem[] = [
        { text: currentBuilding?.summary.name ?? "Byggnad", link: `/building/${buildingKey}` },
        { text: title }
    ];

    if (loadingBuilding || categoriesLoading) {
        return <BreadcrumbComponent items={breadcrumbItems} />;
    }

    if (buildingError) {
        return <div style={{ color: "red" }}>{buildingError}</div>;
    }

    return (
        <div ref={pageRef} className="documents-page" data-mobile-view={mobileView}>
            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <BreadcrumbComponent items={breadcrumbItems} />

            {/* Multi-File Upload Modal */}
            <MultiFileUploadModal
                files={pendingFiles}
                visible={showFileModal}
                defaultCategory={defaultUploadCategory || undefined}
                uploadProgress={uploadProgress}
                isUploading={isUploading}
                onConfirm={handleConfirmMultipleFiles}
                onCancel={handleCancelMultipleFiles}
            />

            {/* File Edit Modal */}
            <DocumentEditModal
                fileName={editDoc?.fileData.name || ""}
                visible={!!editDoc}
                onConfirm={handleEditFileConfirm}
                onCancel={() => setEditDoc(null)}
                isEditMode={true}
                onDelete={handleDeleteDoc}
            />

            {/* Text Document Create Modal */}
            <TextDocumentEditor
                visible={showTextEditor}
                onSave={handleCreateTextDoc}
                onCancel={() => setShowTextEditor(false)}
                isEditMode={false}
            />

            {/* Text Document Edit Modal */}
            <TextDocumentEditor
                visible={!!editingTextDoc}
                onSave={handleEditTextDoc}
                onCancel={() => setEditingTextDoc(null)}
                onDelete={handleDeleteTextDoc}
                isEditMode={true}
                initialData={editingTextDoc ? {
                    title: editingTextDoc.document.name,
                    content: editingTextDoc.document.textContent,
                    category: editingTextDoc.document.category || ''
                } : undefined}
            />

            <div
                className="row flex-grow-1 documents-layout"
                style={{
                    pointerEvents: showFileModal || editDoc || showTextEditor || editingTextDoc ? "none" : "auto",
                    filter: showFileModal || editDoc || showTextEditor || editingTextDoc ? "grayscale(50%) brightness(80%)" : "none",
                }}
            >
                <div
                    className={`col-12 col-md-4 col-lg-3 documents-list-panel ${
                        isDragging ? "bg-info bg-opacity-10 border-2 border-info border-dashed" : ""
                    }`}
                    style={{
                        backgroundColor: isDragging ? undefined : "white",
                        borderRadius: isDragging ? undefined : "10px",
                        boxShadow: isDragging ? undefined : "rgba(0,0,0,0.06) 0 0 16px",
                    }}
                    onClickCapture={rememberListScroll}
                    onDragEnter={(e) => {
                        e.preventDefault();
                        if (allowFileUploads) {
                            setIsDragging(true);
                        }
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        // Reset category highlight when over general area (not a category)
                        if (allowFileUploads && draggingOverCategory !== null) {
                            setDraggingOverCategory(null);
                        }
                    }}
                    onDrop={allowFileUploads ? (e) => handleDrop(e) : undefined}
                    onDragLeave={(e) => {
                        // Only reset if leaving the container entirely
                        const relatedTarget = e.relatedTarget as Node | null;
                        if (!e.currentTarget.contains(relatedTarget)) {
                            setIsDragging(false);
                            setDraggingOverCategory(null);
                        }
                    }}
                >
                    <div className="p-3">
                        {/* Action buttons row */}
                        <div className="d-flex gap-2 mb-3 documents-actions">
                            {allowTextDocuments && (
                                <button
                                    className="btn btn-primary flex-grow-1"
                                    onClick={() => setShowTextEditor(true)}
                                    disabled={showFileModal || !!editDoc || showTextEditor || !!editingTextDoc}
                                    style={{
                                        backgroundColor: "rgb(0, 29, 96)",
                                        border: "none"
                                    }}
                                >
                                    <Plus className="me-2" size={16} />
                                    Skapa textdokument
                                </button>
                            )}
                            {allowFileUploads && isSmallScreen && (
                                <>
                                    <button
                                        className="btn flex-grow-1 documents-upload-button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={showFileModal || !!editDoc || showTextEditor || !!editingTextDoc || isUploading}
                                    >
                                        <Upload className="me-2" size={16} />
                                        Ladda upp filer
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        hidden
                                        onChange={handleFilesPicked}
                                    />
                                </>
                            )}
                        </div>

                        {allowFileUploads && !isSmallScreen && (
                            <div
                                className="text-center text-muted fst-italic py-3 d-flex flex-column align-items-center"
                                style={{ border: "2px dashed #ced4da", borderRadius: "10px" }}
                            >
                                <Upload className="mb-2" size={32} />
                                <span>
                                    Släpp filer här
                                    <br />
                                    för att ladda upp
                                </span>
                                <span className="small mt-2">
                                    (Eller klistra in med Ctrl + V)
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow-1 overflow-auto px-3 pb-3">
                        <div className="filter-panel mt-3 mb-4" style={{ width: '100%', border: '1px solid #e9ecef' }}>
                            <button
                                type="button"
                                className="filter-panel-header"
                                onClick={() => setShowFilters(!showFilters)}
                                aria-expanded={showFilters}
                            >
                                <i className="pi pi-filter filter-panel-icon" />

                                <span className="filter-panel-title" style={{ flex: '0 0 auto' }}>Filter</span>

                                <div className="filter-panel-pills">
                                    {!showFilters && (
                                        filteredOutCount > 0 ? (
                                            <span className="filter-pill">
                                                Visar {selectedSections.size} av {allSections.length}
                                            </span>
                                        ) : (
                                            <span className="text-muted small">Visar alla dokument</span>
                                        )
                                    )}
                                </div>

                                <i className={`pi pi-chevron-${showFilters ? 'up' : 'down'} filter-panel-chevron`} />
                            </button>

                            {showFilters && (
                                <div className="filter-panel-body">
                                    <div className="filter-panel-controls">
                                        <h6 className="small text-muted text-uppercase mb-2">Dokumentkategorier</h6>
                                        <div className="d-flex flex-column gap-1 mb-3">
                                            {customFileTypeOptions.map(option => (
                                                <div key={option.value} className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`filter-${option.value}`}
                                                        checked={selectedSections.has(option.value)}
                                                        onChange={() => handleSectionToggle(option.value)}
                                                        style={selectedSections.has(option.value) ? { backgroundColor: 'rgb(0, 29, 96)', borderColor: 'rgb(0, 29, 96)' } : undefined}
                                                    />
                                                    <label
                                                        className="form-check-label small"
                                                        htmlFor={`filter-${option.value}`}
                                                    >
                                                        {option.label} ({categoryCount[option.value] || 0})
                                                    </label>
                                                </div>
                                            ))}
                                        </div>

                                        <h6 className="small text-muted text-uppercase mb-2">Övriga sektioner</h6>
                                        <div className="d-flex flex-column gap-1">
                                            {SPECIAL_SECTIONS.filter(section => tenantsEnabled || section !== 'Hyresavtal').map(section => (
                                                <div key={section} className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`filter-${section}`}
                                                        checked={selectedSections.has(section)}
                                                        onChange={() => handleSectionToggle(section)}
                                                        style={selectedSections.has(section) ? { backgroundColor: 'rgb(0, 29, 96)', borderColor: 'rgb(0, 29, 96)' } : undefined}
                                                    />
                                                    <label
                                                        className="form-check-label small"
                                                        htmlFor={`filter-${section}`}
                                                    >
                                                        {section}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <Button
                                        className="filter-panel-clear"
                                        variant="secondary"
                                        disabled={filteredOutCount === 0}
                                        onClick={() => {
                                            handleResetFilters();
                                            setShowFilters(false);
                                        }}
                                    >
                                        <i className="pi pi-times-circle" style={{ marginRight: '8px' }} />
                                        Rensa filter
                                    </Button>
                                </div>
                            )}
                        </div>

                        {Object.entries(groupedDocuments)
                            .filter(([category]) => selectedSections.has(category))
                            .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
                            .map(([category, documentsInCategory]) => (
                                <div
                                    key={category}
                                    className={`mb-4 p-2 rounded ${draggingOverCategory === category ? "bg-info bg-opacity-25 border border-info border-dashed" : ""}`}
                                    style={{ margin: "-0.5rem", marginBottom: "1rem" }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (allowFileUploads && draggingOverCategory !== category) {
                                            setDraggingOverCategory(category);
                                        }
                                    }}
                                    onDrop={allowFileUploads ? (e) => handleDrop(e, category) : undefined}
                                >
                                    <button
                                        className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                        onClick={() => toggleSectionCollapse(category)}
                                        style={{ border: "none", background: "none" }}
                                    >
                                        <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                            <span>
                                                {getCategoryDisplayName(category)} ({documentsInCategory.length})
                                            </span>
                                            {collapsedSections[category] ? (
                                                <ChevronRight size={16} />
                                            ) : (
                                                <ChevronDown size={16} />
                                            )}
                                        </h5>
                                    </button>

                                    {!collapsedSections[category] && (
                                        <div className="list-group list-group-flush">
                                            {documentsInCategory.map(({ dbKey, doc }) => (
                                                <div key={dbKey} className="d-flex align-items-center gap-2 mb-2">
                                                    <div
                                                        className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                            (isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                            (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                ? "active" : ""
                                                        }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            whiteSpace: "normal",
                                                            overflowWrap: "break-word",
                                                            wordBreak: "break-word",
                                                            backgroundColor: (isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                ? "rgb(0, 29, 96)" : undefined,
                                                            color: (isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                ? "white" : undefined,
                                                            borderColor: (isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                ? "rgb(0, 29, 96)" : undefined,
                                                        }}
                                                        onClick={() => showDocument(dbKey)}
                                                    >
                                                        {isTextDocument(doc) ? (
                                                            <FileText
                                                                className={(isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                    (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                    ? "" : "text-secondary"}
                                                                size={18}
                                                                color={(isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                    (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                    ? "white" : undefined}
                                                            />
                                                        ) : (
                                                            <File
                                                                className={(isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                    (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                    ? "" : "text-secondary"}
                                                                size={18}
                                                                color={(isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                    (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                    ? "white" : undefined}
                                                            />
                                                        )}
                                                        <span className="flex-grow-1">{doc.name}</span>
                                                    </div>

                                                    <div className="dropdown" style={{ position: 'relative' }}>
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDropdown(openDropdown === dbKey ? null : dbKey);
                                                            }}
                                                        >
                                                            <MoreVertical size={14} />
                                                        </button>
                                                        {openDropdown === dbKey && (
                                                            <ul
                                                                className="dropdown-menu dropdown-menu-end show"
                                                                style={{ position: 'absolute', right: 0, top: '100%' }}
                                                            >
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item d-flex align-items-center gap-2"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenDropdown(null);
                                                                            handleEditClick(dbKey, doc);
                                                                        }}
                                                                    >
                                                                        <Edit3 size={14} />
                                                                        Redigera
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item d-flex align-items-center gap-2"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenDropdown(null);
                                                                            handleDownloadClick(doc);
                                                                        }}
                                                                    >
                                                                        <Download size={14} />
                                                                        Ladda ner
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                        {/* Planritningar Section */}
                        {selectedSections.has("Planritningar") && (
                            <div className="mb-4">
                                <button
                                    className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                    onClick={() => toggleSectionCollapse("Planritningar")}
                                    style={{ border: "none", background: "none" }}
                                >
                                    <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                        <span>
                                            Planritningar ({levelBlueprints.length})
                                        </span>
                                        {collapsedSections["Planritningar"] ? (
                                            <ChevronRight size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </h5>
                                </button>

                                {!collapsedSections["Planritningar"] && (
                                    <>
                                    <p className="text-muted small fst-italic mb-2" style={{ marginTop: '-0.5rem' }}>
                                        Planritningar administreras från varje våningsplan och takplanet
                                    </p>
                                    <div className="list-group list-group-flush">
                                        {levelBlueprints.map(({ levelKey, label, blueprint }) => (
                                            <div key={levelKey} className="d-flex align-items-center gap-2 mb-2">
                                                <div
                                                    className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                        selectedBlueprint === blueprint.storagePath ? "active" : ""
                                                    }`}
                                                    style={{
                                                        cursor: "pointer",
                                                        whiteSpace: "normal",
                                                        overflowWrap: "break-word",
                                                        wordBreak: "break-word",
                                                        backgroundColor: selectedBlueprint === blueprint.storagePath ? "rgb(0, 29, 96)" : undefined,
                                                        color: selectedBlueprint === blueprint.storagePath ? "white" : undefined,
                                                        borderColor: selectedBlueprint === blueprint.storagePath ? "rgb(0, 29, 96)" : undefined,
                                                    }}
                                                    onClick={() => showDocument(levelKey)}
                                                >
                                                    <File
                                                        className={selectedBlueprint === blueprint.storagePath ? "" : "text-secondary"}
                                                        size={18}
                                                        color={selectedBlueprint === blueprint.storagePath ? "white" : undefined}
                                                    />
                                                    <span className="flex-grow-1">{label}</span>
                                                </div>
                                                <div className="dropdown" style={{ position: 'relative' }}>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdown(openDropdown === `blueprint-${levelKey}` ? null : `blueprint-${levelKey}`);
                                                        }}
                                                    >
                                                        <MoreVertical size={14} />
                                                    </button>
                                                    {openDropdown === `blueprint-${levelKey}` && (
                                                        <ul
                                                            className="dropdown-menu dropdown-menu-end show"
                                                            style={{ position: 'absolute', right: 0, top: '100%' }}
                                                        >
                                                            <li>
                                                                <button
                                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown(null);
                                                                        handleDownloadBlueprint(blueprint, label);
                                                                    }}
                                                                >
                                                                    <Download size={14} />
                                                                    Ladda ner
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Arbetsmiljörisker Section */}
                        {selectedSections.has("Arbetsmiljörisker") && (
                            <div className="mb-4">
                                <button
                                    className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                    onClick={() => toggleSectionCollapse("Arbetsmiljörisker")}
                                    style={{ border: "none", background: "none" }}
                                >
                                    <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                        <span>
                                            Arbetsmiljörisker ({riskAttachments.length})
                                        </span>
                                        {collapsedSections["Arbetsmiljörisker"] ? (
                                            <ChevronRight size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </h5>
                                </button>

                                {!collapsedSections["Arbetsmiljörisker"] && (
                                    <>
                                    <p className="text-muted small fst-italic mb-2" style={{ marginTop: '-0.5rem' }}>
                                        Arbetsmiljörisker administreras från varje enskilt rum
                                    </p>
                                    <div className="list-group list-group-flush">
                                        {riskAttachments.map(({ roomKey, room, attachment }) => (
                                            <div key={attachment.key} className="d-flex align-items-center gap-2 mb-2">
                                                <div
                                                    className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                        selectedRiskAttachment === attachment.key ? "active" : ""
                                                    }`}
                                                    style={{
                                                        cursor: "pointer",
                                                        whiteSpace: "normal",
                                                        overflowWrap: "break-word",
                                                        wordBreak: "break-word",
                                                        backgroundColor: selectedRiskAttachment === attachment.key ? "rgb(0, 29, 96)" : undefined,
                                                        color: selectedRiskAttachment === attachment.key ? "white" : undefined,
                                                        borderColor: selectedRiskAttachment === attachment.key ? "rgb(0, 29, 96)" : undefined,
                                                    }}
                                                    onClick={() => showDocument(attachment.key)}
                                                >
                                                    <File
                                                        className={selectedRiskAttachment === attachment.key ? "" : "text-secondary"}
                                                        size={18}
                                                        color={selectedRiskAttachment === attachment.key ? "white" : undefined}
                                                    />
                                                    <span className="flex-grow-1">
                                                        {attachment.displayName || attachment.fileName}
                                                        <small className="d-block" style={{ opacity: 0.7 }}>
                                                            {room.displayName()}
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="dropdown" style={{ position: 'relative' }}>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdown(openDropdown === `risk-${attachment.key}` ? null : `risk-${attachment.key}`);
                                                        }}
                                                    >
                                                        <MoreVertical size={14} />
                                                    </button>
                                                    {openDropdown === `risk-${attachment.key}` && (
                                                        <ul
                                                            className="dropdown-menu dropdown-menu-end show"
                                                            style={{ position: 'absolute', right: 0, top: '100%' }}
                                                        >
                                                            <li>
                                                                <button
                                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown(null);
                                                                        handleDownloadRiskAttachment(attachment);
                                                                    }}
                                                                >
                                                                    <Download size={14} />
                                                                    Ladda ner
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Komponentdokument Section */}
                        {selectedSections.has("Komponentdokument") && (
                            <div className="mb-4">
                                <button
                                    className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                    onClick={() => toggleSectionCollapse("Komponentdokument")}
                                    style={{ border: "none", background: "none" }}
                                >
                                    <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                        <span>
                                            Komponentdokument ({componentDocuments.length})
                                        </span>
                                        {collapsedSections["Komponentdokument"] ? (
                                            <ChevronRight size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </h5>
                                </button>

                                {!collapsedSections["Komponentdokument"] && (
                                    <>
                                    <p className="text-muted small fst-italic mb-2" style={{ marginTop: '-0.5rem' }}>
                                        Komponentdokument administreras från varje komponent
                                    </p>
                                    <div className="list-group list-group-flush">
                                        {componentDocuments.map(({ componentKey, componentType, componentLabel, document }) => (
                                            <div key={document.key} className="d-flex align-items-center gap-2 mb-2">
                                                <div
                                                    className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                        selectedComponentDocument === document.key ? "active" : ""
                                                    }`}
                                                    style={{
                                                        cursor: "pointer",
                                                        whiteSpace: "normal",
                                                        overflowWrap: "break-word",
                                                        wordBreak: "break-word",
                                                        backgroundColor: selectedComponentDocument === document.key ? "rgb(0, 29, 96)" : undefined,
                                                        color: selectedComponentDocument === document.key ? "white" : undefined,
                                                        borderColor: selectedComponentDocument === document.key ? "rgb(0, 29, 96)" : undefined,
                                                    }}
                                                    onClick={() => showDocument(document.key)}
                                                >
                                                    <File
                                                        className={selectedComponentDocument === document.key ? "flex-shrink-0" : "text-secondary flex-shrink-0"}
                                                        size={20}
                                                        color={selectedComponentDocument === document.key ? "white" : undefined}
                                                    />
                                                    <span className="flex-grow-1">
                                                        {document.name}
                                                        <small className="d-block" style={{ opacity: 0.7 }}>
                                                            {getTranslation(componentType)}{componentLabel ? ` – ${componentLabel}` : ''}
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="dropdown" style={{ position: 'relative' }}>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdown(openDropdown === `component-doc-${document.key}` ? null : `component-doc-${document.key}`);
                                                        }}
                                                    >
                                                        <MoreVertical size={14} />
                                                    </button>
                                                    {openDropdown === `component-doc-${document.key}` && (
                                                        <ul
                                                            className="dropdown-menu dropdown-menu-end show"
                                                            style={{ position: 'absolute', right: 0, top: '100%' }}
                                                        >
                                                            <li>
                                                                <button
                                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown(null);
                                                                        handleDownloadComponentDocument(componentKey, document);
                                                                    }}
                                                                >
                                                                    <Download size={14} />
                                                                    Ladda ner
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <Link
                                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                                    to={`/building/${buildingKey}/component/${componentKey}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown(null);
                                                                    }}
                                                                >
                                                                    <ChevronRight size={14} />
                                                                    Gå till komponent
                                                                </Link>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Projektbilagor Section */}
                        {selectedSections.has("Projektbilagor") && (
                            <div className="mb-4">
                                <button
                                    className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                    onClick={() => toggleSectionCollapse("Projektbilagor")}
                                    style={{ border: "none", background: "none" }}
                                >
                                    <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                        <span>
                                            Projektbilagor ({projectAttachments.length})
                                        </span>
                                        {collapsedSections["Projektbilagor"] ? (
                                            <ChevronRight size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </h5>
                                </button>

                                {!collapsedSections["Projektbilagor"] && (
                                    <>
                                    <p className="text-muted small fst-italic mb-2" style={{ marginTop: '-0.5rem' }}>
                                        Projektbilagor administreras från varje projekt
                                    </p>
                                    <div className="list-group list-group-flush">
                                        {projectAttachments.map(({ projectKey, projectTitle, attachment }) => {
                                            const displayName = isImageAttachment(attachment)
                                                ? (attachment.displayName || attachment.fileName || 'Bilaga')
                                                : (attachment.displayName || attachment.fileName);
                                            return (
                                                <div key={attachment.key} className="d-flex align-items-center gap-2 mb-2">
                                                    <div
                                                        className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                            selectedProjectAttachment === attachment.key ? "active" : ""
                                                        }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            whiteSpace: "normal",
                                                            overflowWrap: "break-word",
                                                            wordBreak: "break-word",
                                                            backgroundColor: selectedProjectAttachment === attachment.key ? "rgb(0, 29, 96)" : undefined,
                                                            color: selectedProjectAttachment === attachment.key ? "white" : undefined,
                                                            borderColor: selectedProjectAttachment === attachment.key ? "rgb(0, 29, 96)" : undefined,
                                                        }}
                                                        onClick={() => showDocument(attachment.key)}
                                                    >
                                                        <File
                                                            className={selectedProjectAttachment === attachment.key ? "flex-shrink-0" : "text-secondary flex-shrink-0"}
                                                            size={20}
                                                            color={selectedProjectAttachment === attachment.key ? "white" : undefined}
                                                        />
                                                        <span className="flex-grow-1">
                                                            {displayName}
                                                            <small className="d-block" style={{ opacity: 0.7 }}>
                                                                {projectTitle}
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="dropdown" style={{ position: 'relative' }}>
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDropdown(openDropdown === `project-att-${attachment.key}` ? null : `project-att-${attachment.key}`);
                                                            }}
                                                        >
                                                            <MoreVertical size={14} />
                                                        </button>
                                                        {openDropdown === `project-att-${attachment.key}` && (
                                                            <ul
                                                                className="dropdown-menu dropdown-menu-end show"
                                                                style={{ position: 'absolute', right: 0, top: '100%' }}
                                                            >
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item d-flex align-items-center gap-2"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenDropdown(null);
                                                                            handleDownloadProjectAttachment(attachment);
                                                                        }}
                                                                    >
                                                                        <Download size={14} />
                                                                        Ladda ner
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <Link
                                                                        className="dropdown-item d-flex align-items-center gap-2"
                                                                        to={`/building/${buildingKey}/projects/${projectKey}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenDropdown(null);
                                                                        }}
                                                                    >
                                                                        <ChevronRight size={14} />
                                                                        Gå till projekt
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                        {tenantsEnabled && renderExternalSection("Hyresavtal", leaseContracts, "Hyresavtal administreras från hyresavtalslistan", "Gå till hyresavtal")}
                        {renderExternalSection("Ärenden", workOrderAttachments, "Ärendebilagor administreras från varje ärende", "Gå till ärendet")}
                        {renderExternalSection("Tidslinje", timelineAttachments, "Tidslinjebilagor administreras från varje händelse i tidslinjen", "Gå till tidslinjen")}

                        {/* Underhållsdokument Section */}
                        {selectedSections.has("Underhållsdokument") && (
                            <div className="mb-4">
                                <button
                                    className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                    onClick={() => toggleSectionCollapse("Underhållsdokument")}
                                    style={{ border: "none", background: "none" }}
                                >
                                    <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                        <span>
                                            Underhållsdokument ({maintenanceDocuments.length})
                                        </span>
                                        {collapsedSections["Underhållsdokument"] ? (
                                            <ChevronRight size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </h5>
                                </button>

                                {!collapsedSections["Underhållsdokument"] && (
                                    <>
                                    <p className="text-muted small fst-italic mb-2" style={{ marginTop: '-0.5rem' }}>
                                        Underhållsdokument administreras från underhållsplanen
                                    </p>
                                    <div className="list-group list-group-flush">
                                        {maintenanceDocuments.map((entry, index) => {
                                            const documentName = getMaintenanceDocumentName(entry);
                                            const entryKey = entry.type === 'template'
                                                ? `template-${entry.templateId}-${index}`
                                                : `${entry.type}-${entry.componentKey}-${index}`;
                                            const isSelected = selectedMaintenanceDocument === entry.id;

                                            // Determine subtitle based on type
                                            let subtitle = '';
                                            if (entry.type === 'template') {
                                                subtitle = `Instruktion: ${entry.templateName || 'Okänd mall'}`;
                                            } else if (entry.type === 'activity') {
                                                const componentName = entry.componentType ? getTranslation(entry.componentType) : 'Komponent';
                                                subtitle = `${entry.templateName || 'Aktivitet'} (${componentName})`;
                                            } else if (entry.type === 'replacement') {
                                                const componentName = entry.componentType ? getTranslation(entry.componentType) : 'Komponent';
                                                subtitle = `Utbyte (${componentName})`;
                                            }

                                            return (
                                                <div key={entryKey} className="d-flex align-items-center gap-2 mb-2">
                                                    <div
                                                        className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                            isSelected ? "active" : ""
                                                        }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            whiteSpace: "normal",
                                                            overflowWrap: "break-word",
                                                            wordBreak: "break-word",
                                                            backgroundColor: isSelected ? "rgb(0, 29, 96)" : undefined,
                                                            color: isSelected ? "white" : undefined,
                                                            borderColor: isSelected ? "rgb(0, 29, 96)" : undefined,
                                                        }}
                                                        onClick={() => handleMaintenanceDocumentClick(entry)}
                                                    >
                                                        <File
                                                            className={isSelected ? "" : "text-secondary"}
                                                            size={18}
                                                            color={isSelected ? "white" : undefined}
                                                        />
                                                        <span className="flex-grow-1">
                                                            {documentName}
                                                            <small className="d-block" style={{ opacity: 0.7 }}>
                                                                {subtitle}
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="dropdown" style={{ position: 'relative' }}>
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenDropdown(openDropdown === `maint-${entryKey}` ? null : `maint-${entryKey}`);
                                                            }}
                                                        >
                                                            <MoreVertical size={14} />
                                                        </button>
                                                        {openDropdown === `maint-${entryKey}` && (
                                                            <ul
                                                                className="dropdown-menu dropdown-menu-end show"
                                                                style={{ position: 'absolute', right: 0, top: '100%' }}
                                                            >
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item d-flex align-items-center gap-2"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenDropdown(null);
                                                                            handleDownloadMaintenanceDocument(entry);
                                                                        }}
                                                                    >
                                                                        <Download size={14} />
                                                                        Ladda ner
                                                                    </button>
                                                                </li>
                                                                {entry.componentKey && (
                                                                    <li>
                                                                        <Link
                                                                            className="dropdown-item d-flex align-items-center gap-2"
                                                                            to={`/building/${buildingKey}/maintenance/${entry.componentKey}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setOpenDropdown(null);
                                                                            }}
                                                                        >
                                                                            <ChevronRight size={14} />
                                                                            Gå till underhåll
                                                                        </Link>
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    </>
                                )}
                            </div>
                        )}

                    </div>
                </div>

                <div className="col-12 col-md-8 col-lg-9 documents-preview-panel">
                    {isSmallScreen && hasPreview && (
                        <div className="documents-preview-toolbar">
                            <button type="button" className="documents-back-button" onClick={closeDocument}>
                                <ChevronLeft size={20} aria-hidden="true" />
                                Alla dokument
                            </button>
                            {selectedPdf && (
                                // iOS shows only the first page of a PDF in a frame; the browser's own viewer shows all of it.
                                <a className="documents-open-link" href={selectedPdf} target="_blank" rel="noopener noreferrer">
                                    Öppna
                                    <ExternalLink size={16} aria-hidden="true" />
                                </a>
                            )}
                        </div>
                    )}
                    <div className="documents-preview-body">
                    {selectedBlueprint ? (
                        <ImagePreview storagePath={selectedBlueprint} />
                    ) : selectedPdf && selectedFileDoc ? (
                        // Check if it's an image file document
                        (() => {
                            const doc = openBuildingDocument;
                            if (isFileDocument(doc) && isImageFileType(doc.fileType)) {
                                return <ImagePreview imageUrl={selectedPdf} />;
                            }
                            // PDF or other file types use iframe
                            return (
                                <iframe
                                    src={selectedPdf}
                                    className="w-100 h-100 border-0 d-block"
                                    title="PDF Viewer"
                                />
                            );
                        })()
                    ) : selectedPdf ? (
                        // Fallback for PDFs without document context
                        <iframe
                            src={selectedPdf}
                            className="w-100 h-100 border-0 d-block"
                            title="PDF Viewer"
                        />
                    ) : selectedTextDoc ? (
                        <div className="card h-100">
                            <div className="card-body overflow-auto p-4">
                                {(() => {
                                    const doc = openBuildingDocument;
                                    if (isTextDocument(doc)) {
                                        return (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center mb-4">
                                                    <h3 className="card-title mb-0">{doc.name}</h3>
                                                    <div className="text-muted small">
                                                        <FileText className="me-1" size={14} />
                                                        Textdokument
                                                    </div>
                                                </div>
                                                
                                                <div className="alert alert-light border mb-4">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <small className="text-muted">
                                                                <Calendar className="me-1" size={12} />
                                                                <strong>Skapad:</strong> {doc.uploadedAt ? `${new Date(doc.uploadedAt).toLocaleDateString('sv-SE')} ${new Date(doc.uploadedAt).toLocaleTimeString('sv-SE')}` : 'Okänt'}
                                                                {doc.uploadedBy && (
                                                                    <> av <UserName uid={doc.uploadedBy} /></>
                                                                )}
                                                            </small>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <small className="text-muted">
                                                                <Calendar className="me-1" size={12} />
                                                                <strong>Uppdaterad:</strong> {doc.lastModified ? `${new Date(doc.lastModified).toLocaleDateString('sv-SE')} ${new Date(doc.lastModified).toLocaleTimeString('sv-SE')}` : 'Okänt'}
                                                                {doc.lastModifiedBy && (
                                                                    <> av <UserName uid={doc.lastModifiedBy} /></>
                                                                )}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="content-area" style={{
                                                    whiteSpace: "pre-wrap",
                                                    lineHeight: "1.6",
                                                    fontSize: "16px"
                                                }}>
                                                    {doc.textContent}
                                                </div>
                                            </>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </div>
                    ) : previewStoragePath ? (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <div className="text-center text-muted">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Laddar...</span>
                                </div>
                                <p className="mt-2">Laddar dokument...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <div className="text-center text-muted">
                                <FileText className="mx-auto mb-3" size={64} />
                                <h4>Välj en fil, ritning eller textdokument att förhandsvisa</h4>
                                <p className="mb-0">Klicka på ett dokument eller en ritning i listan till vänster för att visa innehållet här.</p>
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentsPage;